/**
 * Cliente del lead (front). Construye el payload del contrato (03_PAYLOAD_CONTRACT.md)
 * y lo postea a POST /api/lead. UTM se lee de la URL.
 */
export type LeadSource =
  | "simple_form"
  | "estimator"
  | "quiz"
  | "snap_envision"
  | "gallery"
  | "consultation"
  | "paid";

export type InvestmentBand = "A" | "B" | "C" | null;
export type Timeline = "asap" | "1-3m" | "exploring" | null;

export interface BathroomScope {
  room?: string;
  scope?: string;
  shower?: string;
  tub?: string;
  vanity?: string;
  floor?: string;
  walls?: string;
  fixtures?: string;
  layout?: string;
  base?: string;
  style?: string;
  extras?: string[];
}

export interface LeadPayload {
  lead_source: LeadSource;
  first_name: string;
  phone: string;
  email: string;
  address: string;
  zip: string;
  bathroom_scope: BathroomScope | null;
  investment_band: InvestmentBand;
  estimator_range: string | null;
  timeline: Timeline;
  consent: boolean;
  /** Gate de lujo: el rango all-in alcanza el mínimo ($25K). false = below-min. */
  qualified?: boolean;
  /** El lead usó el escape hatch "mi presupuesto es flexible". */
  budget_flexible?: boolean;
  utm: { source?: string; campaign?: string; adset?: string; ad?: string };
  /** Tracking (F1.4) — opcionales, aditivos al contrato. Habilitan dedup Pixel↔CAPI. */
  event_id?: string;
  fbp?: string;
  fbc?: string;
  page_url?: string;
}

/** Genera un event_id único (para deduplicar Pixel client-side vs CAPI server-side). */
export function newEventId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** Lee los UTM de la query string (source/campaign/adset/ad; acepta utm_ y sin prefijo). */
export function readUtm(): LeadPayload["utm"] {
  const p = new URLSearchParams(window.location.search);
  const g = (k: string) => p.get(`utm_${k}`) ?? p.get(k) ?? undefined;
  return { source: g("source"), campaign: g("campaign"), adset: g("adset"), ad: g("ad") };
}

export interface SubmitResult {
  ok: boolean;
  status: number;
  data?: unknown;
  error?: string;
}

/** Postea el lead al webhook. No lanza: devuelve un resultado tipado. */
export async function submitLead(payload: LeadPayload): Promise<SubmitResult> {
  try {
    const res = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => undefined);
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    return { ok: false, status: 0, error: err instanceof Error ? err.message : "network error" };
  }
}
